import React, { useMemo, useState, useEffect } from "react";
import { Button } from "reactstrap";
import { useDispatch } from "react-redux";
import LessonDataTable from "components/TableContainers/LessonDataTable";
import EditLessonModal from "./EditLessonModal";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import {
  updateLessonRequest,
  deleteLessonRequest,
} from "store/actions";
import { FaRegEdit } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";

const LessonTable = ({
  lessons,
  loading,
  totalrows,
  courseOptions,
  creatorOptions,
  selectedCreator,
  setSelectedCreator,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  selectedCourse,
  setSelectedCourse,
  selectedModule,
  setSelectedModule,
  selectedChapter,
  setSelectedChapter,
  searchString,
  setSearchString,
  selectedSortData,
  setSelectedSortData
}) => {
  const dispatch = useDispatch();
  const [editData, setEditData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  

  // ──────────────────────────────── 
  // Permissions
  // ────────────────────────────────
  const permissions = JSON.parse(localStorage.getItem("permissions"));
  const hasEditPermission = permissions?.some(
    (p) => p.permission_name === "Edit Lesson"
  );
  const hasDeletePermission = permissions?.some(
    (p) => p.permission_name === "Delete Lesson"
  );

  // ────────────────────────────────
  // Handle Delete
  // ────────────────────────────────
  useEffect(() => {
    if (deleteId && confirmAction) {
      dispatch(deleteLessonRequest(deleteId));
      setOpenModal(false);
      setConfirmAction(false);
    }
  }, [confirmAction, deleteId, dispatch]);

  // ────────────────────────────────
  // Columns Definition
  // ────────────────────────────────
  const columns = useMemo(
    () => [
      { header: "Title", accessorKey: "title" },
      { header: "Course", accessorKey: "chapterId.moduleId.courseId.title" },
      { header: "Module", accessorKey: "chapterId.moduleId.title" },
      { header: "Chapter", accessorKey: "chapterId.title" },
      { header: "Order", accessorKey: "orderIndex" },
      { header: "Type", accessorKey: "contentType" },
      { header: "Created By", accessorKey: "createdBy.name" }, // ✅ Show creator name

      ...(hasEditPermission || hasDeletePermission
        ? [
            {
              header: "Actions",
              id: "actions",
              cell: ({ row }) => {
                const handleEdit = () => {
                  setEditData(row.original);
                  setIsOpen(true);
                };
                const handleDelete = () => {
                  setDeleteId(row.original._id);
                  setOpenModal(true);
                };
                return (
                  <div className="d-flex gap-2">
                    {hasEditPermission && (
                      <Button
                        color="primary"
                        onClick={handleEdit}
                        title="Edit"
                      >
                        <FaRegEdit size={18} />
                      </Button>
                    )}
                    {hasDeletePermission && (
                      <Button
                        color="danger"
                        onClick={handleDelete}
                        title="Delete"
                      >
                        <MdDeleteOutline size={18} />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          ]
        : []),
    ],
    [hasEditPermission, hasDeletePermission]
  );

  // ────────────────────────────────
  // Update Handler
  // ────────────────────────────────
  const handleUpdate = (id, data, onClose) => {
    dispatch(
      updateLessonRequest(id, data, (err) => {
        if (!err) onClose();
      })
    );
  };

  // ────────────────────────────────
  // UI
  // ────────────────────────────────
  return (
    <>
      <ConfirmationModal
        okText="Confirm"
        onCancel={() => setOpenModal(false)}
        onOk={() => setConfirmAction(true)}
        isVisible={openModal}
        title="Delete Lesson"
        content="Are you sure you want to delete this lesson?"
      />

      <EditLessonModal
        visible={isOpen}
        initialData={editData}
        onSubmit={handleUpdate}
        handleClose={() => setIsOpen(false)}
        courseOptions={courseOptions}
      />

      <LessonDataTable
        columns={columns}
        loading={loading}
        data={lessons || []}
        courseOptions={courseOptions}
        creatorOptions={creatorOptions}
        selectedCreator={selectedCreator}
        setSelectedCreator={setSelectedCreator}
        isGlobalFilter
        isPagination
        totalrows={totalrows}
        SearchPlaceholder="Search lessons..."
        selectedSortData={selectedSortData}
        setSelectedSortData={setSelectedSortData}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        searchString={searchString}
        setSearchString={setSearchString}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        selectedModule={selectedModule}
        setSelectedModule={setSelectedModule}
        selectedChapter={selectedChapter}
        setSelectedChapter={setSelectedChapter}
      />
    </>
  );
};

export default LessonTable;
